import React, { useState, useEffect, useCallback } from 'react';
import { Guid } from '@Shared.Angular/@types/guid';
import {
  DeleteEntityTypes,
  FlowinglyPermissions
} from '@Shared.Angular/flowingly.services/flowingly.constants';
import { SelectUser } from '@Shared.Angular/flowingly.components/SelectUser/SelectUser';
import { KendoReactComboBox } from '@Shared.Angular/kendo.react/KendoReactComboBox';
import { ComboBoxChangeEvent } from '@progress/kendo-react-dropdowns';
import IGroupDetail from '@Shared.Angular/@types/core/contracts/queryModel/group/groupDetail';
import './RunnerSetupDeleteSearch.scss';
import { IEntityToDelete } from './RunnerSetupDeleteSearch';

interface IActorSelection {
  actor?: Guid;
  manager?: Guid;
  customdatabaseactor?: Guid;
  categoryApprover?: Guid;
  stepTaskUserId?: Guid;
}

type Props = {
  entityToDelete: IEntityToDelete;
  entityName: string;
  teams: IGroupDetail[];
  reassignActorRequired: boolean;
  onActorChosen: (actors: IActorSelection) => void;
  reassignCustomDatabaseRequired: boolean;
  reassignCategoryApproverRequired: boolean;
  reassignStepTasksRequired: boolean;
};

export const RunnerSetupDeleteSearchUser = ({
  entityToDelete,
  entityName,
  teams,
  reassignActorRequired,
  onActorChosen,
  reassignCustomDatabaseRequired,
  reassignCategoryApproverRequired,
  reassignStepTasksRequired
}: Props) => {
  const [actorType, setActorType] = useState<DeleteEntityTypes>(
    DeleteEntityTypes.userEntityType
  );
  const [selectedActorId, setSelectedActorId] = useState<Guid | undefined>(
    undefined
  );
  const [selectedTeam, setSelectedTeam] = useState<IGroupDetail | null>(null);
  const [selectedManagerId, setSelectedManagerId] = useState<Guid | undefined>(
    undefined
  );
  const [selectedCustomDatabaseId, setSelectedCustomDatabaseId] = useState<
    Guid | undefined
  >(undefined);
  const [selectedCategoryApproverId, setSelectedCategoryApproverId] = useState<
    Guid | undefined
  >(undefined);
  const [selectedStepTasksUserId, setSelectedStepTasksUserId] = useState<
    Guid | undefined
  >(undefined);

  const approverPermissions = [
    FlowinglyPermissions.GOVERNANCE_FLOWMODEL_PUBLISHER_CANDIDATE
  ];

  const raiseActorChange = useCallback(() => {
    onActorChosen({
      actor: selectedActorId,
      manager: selectedManagerId,
      customdatabaseactor: selectedCustomDatabaseId,
      categoryApprover: selectedCategoryApproverId,
      stepTaskUserId: selectedStepTasksUserId
    });
  }, [
    selectedActorId,
    selectedManagerId,
    selectedCustomDatabaseId,
    selectedCategoryApproverId,
    selectedStepTasksUserId,
    onActorChosen
  ]);

  useEffect(() => {
    raiseActorChange();
  }, [raiseActorChange]);

  const handleRadioChange = (newActorType: DeleteEntityTypes) => {
    setActorType(newActorType);
    setSelectedActorId(undefined);
    setSelectedTeam(null);
  };

  const handleTeamChange = (event: ComboBoxChangeEvent) => {
    const team = event.value as IGroupDetail;
    setSelectedTeam(team);
    if (team) {
      setSelectedActorId(team.id);
    }
  };

  return (
    <div className="delete-search">
      <div className="search-box">
        {/* REASSIGN ACTOR SECTION */}
        {reassignActorRequired && (
          <div className="search-row">
            <div className="search-column">
              <label className="row-title">Reassign Actor</label>
              <p>
                To ensure in-progress & published flows continue to work, search
                for a User or Team, to replace <b>{entityName}</b>
              </p>
            </div>
            <div className="search-column">
              <ul className="actor-list">
                <li>
                  <input
                    type="radio"
                    name="actorType"
                    id="radiouser"
                    checked={actorType === DeleteEntityTypes.userEntityType}
                    value={DeleteEntityTypes.userEntityType}
                    onChange={() => {
                      handleRadioChange(DeleteEntityTypes.userEntityType);
                    }}
                  />
                  <label htmlFor="radiouser">User</label>
                </li>
                {teams.length > 0 && (
                  <li>
                    <input
                      type="radio"
                      name="actorType"
                      id="radiogroup"
                      checked={actorType === DeleteEntityTypes.teamEntityType}
                      value={DeleteEntityTypes.teamEntityType}
                      onChange={() => {
                        handleRadioChange(DeleteEntityTypes.teamEntityType);
                      }}
                    />
                    <label htmlFor="radiogroup">Team</label>
                  </li>
                )}
              </ul>
              <div>
                {actorType === DeleteEntityTypes.userEntityType && (
                  <SelectUser
                    selectedUserId={selectedActorId}
                    onUserSelect={setSelectedActorId}
                    containerClass="width-100"
                    exclude={entityToDelete.id}
                    excludeInvitedUsers={true}
                    showEmail={true}
                    inputId="reassign-actor-user-select"
                  />
                )}
                {actorType === DeleteEntityTypes.teamEntityType && (
                  <KendoReactComboBox
                    data={teams}
                    textField="name"
                    dataItemKey="id"
                    placeholder="Select Team"
                    value={selectedTeam}
                    onChange={handleTeamChange}
                  />
                )}
              </div>
            </div>
          </div>
        )}
        {/* END REASSIGN ACTOR SECTION */}

        {/* REASSIGN MANAGER SECTION */}
        {entityToDelete.isManager && (
          <div className="search-row">
            <div className="search-column">
              <label className="row-title">Reassign Manager</label>
              <p>
                {entityName} is a manager. Select a user to replace {entityName}
                .
              </p>
            </div>
            <div className="search-column">
              <label>User </label>
              <SelectUser
                selectedUserId={selectedManagerId}
                onUserSelect={setSelectedManagerId}
                exclude={entityToDelete.id}
                excludeInvitedUsers={true}
                showEmail={true}
                containerClass="width-100"
                inputId="reassign-manager-user-select"
              />
            </div>
          </div>
        )}
        {/*END REASSIGN MANAGER SECTION */}

        {/* REASSIGN CUSTOM DATABASE SECTION */}
        {reassignCustomDatabaseRequired && (
          <div className="search-row">
            <div className="search-column">
              <label className="row-title">
                Reassign User in Custom Databases
              </label>
              <p>
                {entityName} is a user in a database. Select another user to
                replace {entityName}.
              </p>
            </div>
            <div className="search-column">
              <label>User </label>
              <SelectUser
                selectedUserId={selectedCustomDatabaseId}
                onUserSelect={setSelectedCustomDatabaseId}
                exclude={entityToDelete.id}
                excludeInvitedUsers={true}
                showEmail={true}
                containerClass="width-100"
                inputId="reassign-custom-database-user-select"
              />
            </div>
          </div>
        )}
        {/*END CUSTOM DATABASE SECTION */}

        {/* REASSIGN CATEGORY APPROVER SECTION */}
        {reassignCategoryApproverRequired && (
          <div className="search-row">
            <div className="search-column">
              <label className="row-title">Reassign Category Publisher</label>
              <p>
                {entityName} is a category publisher. Select a user to replace{' '}
                {entityName}.
              </p>
            </div>
            <div className="search-column">
              <label>User </label>
              <SelectUser
                selectedUserId={selectedCategoryApproverId}
                onUserSelect={setSelectedCategoryApproverId}
                exclude={entityToDelete.id}
                excludeInvitedUsers={true}
                showEmail={true}
                containerClass="width-100"
                permissionSecureCodes={approverPermissions}
                inputId="reassign-category-approver-user-select"
              />
            </div>
          </div>
        )}
        {/*END REASSIGN CATEGORY APPROVER SECTION */}

        {/* REASSIGN STEP TASKS SECTION */}
        {reassignStepTasksRequired && (
          <div className="search-row">
            <div className="search-column">
              <label className="row-title">Reassign User's Step Tasks</label>
              <p>
                {entityName} is involved with active Step Tasks. Select another
                user to replace {entityName}.
              </p>
            </div>
            <div className="search-column">
              <label>User </label>
              <SelectUser
                selectedUserId={selectedStepTasksUserId}
                onUserSelect={setSelectedStepTasksUserId}
                exclude={entityToDelete.id}
                excludeInvitedUsers={true}
                showEmail={true}
                containerClass="width-100"
                inputId="reassign-step-tasks-user-select"
              />
            </div>
          </div>
        )}
        {/*END REASSIGN STEP TASKS SECTION */}
      </div>
    </div>
  );
};
